// @ts-strict-ignore
import { Contentful } from '@t/contentful'
import { FsCore } from '@t/fs-core'
import { contentId } from '@u/contentful'
import cleansedUserInputs from '@u/parse-calculator-inputs'
import { getCalculatorInputs } from './calculators/calculators-selectors'
import { parsedHitsWithExtraData, getESQuery } from './elasticsearch/elasticsearch-selectors'

// This empty object should probably not be a default in the first place, but for now leaving things as they were
const defaultState = {} as FsCore.State

export const state = (s: FsCore.State = defaultState) => s.contentful || {}

export const breadcrumb = (s: FsCore.State) => state(s).breadcrumb

const payload = (s: FsCore.State = defaultState) => state(s).ctx || {}

export const findEntityBySysId = <T>(
  s: FsCore.State = defaultState,
  id: string,
): Contentful.TypedModule<T> =>
  // /!\ We should handle this default empty object better
  (Object.values(includes(s)).find(
    (entity) => contentId(entity) === id,
  ) as Contentful.TypedModule<T>) || ({} as Contentful.TypedModule<T>)

export const findEntityFieldsBySysId = <T>(s: FsCore.State = defaultState, id: string): T =>
  (findEntityBySysId(s, id).fields as T) || ({} as T)

export const findEntitiesFields = (s: FsCore.State = defaultState, entries: Contentful.Module[]) =>
  entries.map((entry) => findEntityBySysId(s, contentId(entry)).fields || {})

export const findEntities = (s: FsCore.State = defaultState, entries: Contentful.Module[]) =>
  entries.map((entry) => findEntityBySysId(s, contentId(entry)))

export const includes = (s: FsCore.State = defaultState) => payload(s).includes || {}
// Alias to the function above because its name is confusing and often clashes with variables
export const getIncludes = includes

type ProductTableDatasetId = string
export const productTable = (s: FsCore.State = defaultState, key?: ProductTableDatasetId) => {
  return (key ? state(s).productTables[key] : state(s).productTable) || {}
}

export const getModuleFromIncludes = (includes: Contentful.Includes, entityId: string) =>
  includes[entityId] || {}

export const schema = (s: FsCore.State = defaultState) => productTable(s).fields.schema || {}

export const linkTransformation = (s: FsCore.State = defaultState) =>
  schema(s)?.linkTransformation || {}

export const dates = (s: FsCore.State = defaultState) => state(s).dates
export const updatedAt = (s: FsCore.State = defaultState) => dates(s).updated
export const createdAt = (s: FsCore.State = defaultState) => dates(s).created

export const getHitsWithExtraData = (state: FsCore.State, schemaId: string, dataId: string) => {
  const schemaObject = productTable(state, schemaId).fields.schema
  const calculatorId = getESQuery(state, dataId)?.calculatorId
  let cleansedInputs: FsCore.CalculatorInputs | undefined
  if (calculatorId) {
    // Some tables won't have this because they have completely static data with no calculator
    const calculatorInputs = getCalculatorInputs(state, calculatorId)
    cleansedInputs = cleansedUserInputs(calculatorInputs)
  }

  return parsedHitsWithExtraData(state, cleansedInputs, schemaObject, dataId) || []
}

export const url = (s: FsCore.State = defaultState) => state(s).url

export const hydrations = (s: FsCore.State = defaultState) => state(s).hydrations

export const rootEntity = (s: FsCore.State = defaultState) => payload(s).items[0]
